import React, { useEffect, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { PageContainer, SelectFilter } from "@outpave/ds";
import StatusType from "enums/CardStatusType";
import CardType from "enums/CardType";
import Role from "enums/Role";
import useDebouncedSearch from "hooks/useDebouncedSearch";
import useGetParamFromUrl from "hooks/useGetParamFromUrl";
import { useAdminTools } from "hooks/useAdminTools";
import { useAppEntitlements } from "hooks/useAppEntitlements";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import CardDetailsDrawer from "components/common/cards/CardDetailsDrawer";
import CardFormDrawer from "components/common/cards/CardFormDrawer";
import CardsTable from "components/common/cards/CardsTable";
import PendingCardApprovalTable from "components/admin/cards/PendingCardApprovalTable";
import { useCardholderOptions } from "components/admin/cards/useCardholderOptions";
import {
  formatRequestCount,
  usePendingRequestCount,
} from "components/admin/cards/usePendingRequestCount";
import {
  CARD_TAB_STATUSES,
  cardStatusOptions,
  cardTypeOptions,
  composeCardStatus,
  narrowToTab,
} from "utils/cards/cardFilters";
import type { CardFilterCriteria } from "utils/cards/cardFilters";

const CARD_TABS = {
  ACTIVE_PENDING: "active-pending",
  ALL: "all",
  PENDING_APPROVAL: "pending-approval",
} as const;

interface CardsPageProps {
  /** Only the viewer's own cards, whatever their role. */
  mine?: boolean;
}

const CardsPage: React.FC<CardsPageProps> = ({ mine = false }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const adminTools = useAdminTools();
  const isAdminOrHigher = useCheckUserRole(Role.Admin);
  const isAuditor = useCheckUserRole(Role.Auditor, { exact: true });
  const isAccountant = useCheckUserRole(Role.Accountant, { exact: true });
  const { spend, byoc } = useAppEntitlements();

  const activeTabFromState =
    typeof (location.state as { activeTab?: unknown } | null)?.activeTab ===
    "number"
      ? (location.state as { activeTab: number }).activeTab
      : 0;
  const [activeTab, setActiveTab] = useState<number>(activeTabFromState);
  const [searchTerm, setSearchTerm] = useState("");
  const search = useDebouncedSearch(setSearchTerm);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [cardholderFilter, setCardholderFilter] = useState<string | null>(null);
  const [refreshCards, setRefreshCards] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [openCardId, setOpenCardId] = useState<string | null>(null);

  // /my/cards?id=<cardId> and /admin/cards?id=<cardId> open that card.
  const resetParam = useGetParamFromUrl("id", setOpenCardId);

  const pendingRequestCount = usePendingRequestCount(refreshCards, {
    skip: !adminTools,
  });
  const cardholderOptions = useCardholderOptions({ skip: !adminTools });

  const tabConfigurations = useMemo(
    () => [
      {
        id: CARD_TABS.ACTIVE_PENDING,
        label: t("Active/Pending"),
        status: CARD_TAB_STATUSES.activePending,
        isVisible: true,
      },
      {
        id: CARD_TABS.ALL,
        label: t("Show All"),
        status: CARD_TAB_STATUSES.all,
        isVisible: true,
      },
      {
        id: CARD_TABS.PENDING_APPROVAL,
        label: pendingRequestCount
          ? `${t("Requests")} · ${formatRequestCount(pendingRequestCount)}`
          : t("Requests"),
        status: CARD_TAB_STATUSES.all,
        isVisible: adminTools,
      },
    ],
    [t, adminTools, pendingRequestCount],
  );

  const activeTabConfig = tabConfigurations[activeTab];
  const tabStatuses = activeTabConfig?.status ?? CARD_TAB_STATUSES.all;

  useEffect(() => {
    if (!activeTabConfig?.isVisible) {
      setActiveTab(tabConfigurations.findIndex((tab) => tab.isVisible));
    }
  }, [tabConfigurations, activeTabConfig]);

  const statusOptions = useMemo(
    () => cardStatusOptions(tabStatuses, t),
    [tabStatuses, t],
  );
  const typeOptions = useMemo(() => cardTypeOptions(t), [t]);

  const effectiveStatusFilter = useMemo(
    () => narrowToTab(statusFilter, tabStatuses),
    [statusFilter, tabStatuses],
  );

  const filterData: CardFilterCriteria = useMemo(
    () => ({
      cardStatus: composeCardStatus(
        tabStatuses,
        effectiveStatusFilter as StatusType[],
      ),
      cardType: typeFilter as CardType[],
      userId: adminTools ? cardholderFilter : null,
      searchTerm,
    }),
    [
      tabStatuses,
      effectiveStatusFilter,
      typeFilter,
      adminTools,
      cardholderFilter,
      searchTerm,
    ],
  );

  const filterLabels = useMemo(
    () => ({
      search: (label: string) =>
        t("SelectFilterSearch", { label: label.toLowerCase() }),
      noMatches: (label: string, query: string) =>
        t("SelectFilterNoMatches", { label: label.toLowerCase(), query }),
      available: (total: number) => t("SelectFilterAvailable", { total }),
    }),
    [t],
  );

  const refresh = () => setRefreshCards((prev) => prev + 1);

  // Outpave issues these cards, so a company without the Spend app has none to add.
  const canAddCard = spend && !(adminTools && (isAuditor || isAccountant));
  const onRequestsTab =
    activeTabConfig?.id === CARD_TABS.PENDING_APPROVAL &&
    activeTabConfig.isVisible;

  return (
    <PageContainer
      title={t("Cards")}
      primaryAction={
        canAddCard && (
          <Button onClick={() => setCreateOpen(true)}>
            {adminTools ? t("Create Card") : t("Add Card")}
          </Button>
        )
      }
      secondaryAction={
        adminTools &&
        byoc &&
        isAdminOrHigher && (
          <Button
            variant="primary-outlined"
            onClick={() => navigate("/admin/settings/linked-accounts")}
          >
            {t("Link Your Card")}
          </Button>
        )
      }
      tabs={{
        items: tabConfigurations
          .map((tab, index) => ({ tab, index }))
          .filter(({ tab }) => tab.isVisible)
          .map(({ tab, index }) => ({ id: String(index), label: tab.label })),
        activeId: String(activeTab),
        onChange: (id) => setActiveTab(Number(id)),
      }}
      search={{ ...search, placeholder: t("Search") }}
      filters={
        onRequestsTab ? undefined : (
          <>
            {adminTools && (
              <SelectFilter
                multiple
                label={t("Cardholder")}
                options={cardholderOptions}
                selected={cardholderFilter ? [cardholderFilter] : []}
                onChange={(values) =>
                  setCardholderFilter(values[values.length - 1] ?? null)
                }
                labels={filterLabels}
              />
            )}
            <SelectFilter
              multiple
              label={t("Card type")}
              options={typeOptions}
              selected={typeFilter}
              onChange={setTypeFilter}
              labels={filterLabels}
            />
            <SelectFilter
              multiple
              label={t("Status")}
              options={statusOptions}
              selected={effectiveStatusFilter}
              onChange={setStatusFilter}
              labels={filterLabels}
            />
          </>
        )
      }
    >
      {onRequestsTab ? (
        <PendingCardApprovalTable
          handleOpenCardDetails={setOpenCardId}
          refreshCards={refreshCards}
          filterData={filterData}
        />
      ) : (
        <CardsTable
          filterData={filterData}
          mine={mine}
          handleOpenCardDetails={setOpenCardId}
          refreshCards={refreshCards}
        />
      )}

      <CardDetailsDrawer
        open={openCardId !== null}
        cardId={openCardId}
        onClose={(changed) => {
          setOpenCardId(null);
          resetParam();
          if (changed) refresh();
        }}
      />

      <CardFormDrawer
        open={createOpen}
        onClose={(changed) => {
          setCreateOpen(false);
          if (changed) refresh();
        }}
      />
    </PageContainer>
  );
};

export default CardsPage;
